/* eslint-disable no-shadow */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';

import PopOverMenu from '../../../../../components/PopOverMenu';
import Utr from '../../../../../components/text/Utr';
import { drawPositionTypeIds, sportTypeIds } from '../../../../../utils/eventConstants';
import { MatchFormatTypes } from '../../../../../utils/postScoreConstants';
import { capitalize } from '../../../../../utils/formatting';
import { getAveragePickleballRatingDisplayV2 } from '../../../../../utils/events';
import { getTiebreak } from '../../../../../utils';
import {
    getMatchWinnerPositions,
    getMatchSheetTeamDisplayName,
    getMatchSheetTeamAvgDoublesUtr,
} from '../../../../../utils/matchCardUtils';
import { getDrawName, TBD_DISPLAY } from '../../../../../utils/courtSchedular';
import { getRoundLabel } from '../../../../../utils/roundLabel';

import styles from './scheduleTab.scss';

const MatchSheetCard = (props) => {
    const {
        match,
        i,
        eventTimeZone,
        hidePrintButton,
        menuOptions,
        onMenuSelected,
        isOfficialHSEvent,
        isCurrentMemberAdmin,
        postScoreEnabled,
        onPostScoreClick,
        sportTypeId,
        drawCategoryTypesDict,
        showRoundColumn,
        roundSize,
    } = props;

    const draws = useSelector(({ eventProfile }) => eventProfile.draws);

    const memberById = useMemo(
        () => new Map((match.participatingMembers ?? []).map((m) => [m.id, m])),
        [match.participatingMembers],
    );

    const [didPosition1Win, didPosition2Win] = getMatchWinnerPositions(match);
    const isTennisEvent = sportTypeId === sportTypeIds.TENNIS;

    const getMatchLocation = () => {
        const facilityAbbreviation = match.facilityAbbreviation || '';
        const courtName = match.courtName || '';
        return `${facilityAbbreviation} ${courtName}`.trim();
    };

    const buildDrawName = () => {
        const childDraw = draws.drawsDict[match.drawId];
        if (childDraw?.parentDrawId) {
            const parentDraw = draws.drawsDict[childDraw.parentDrawId];
            return `${parentDraw?.name} (${getDrawName(childDraw, parentDraw, drawCategoryTypesDict)})`;
        }
        return childDraw?.name;
    };

    const renderRating = (player, ratingType, column) => {
        if (!player) {
            return null;
        }
        return (
            <span className={`${styles.rating} ${column === 'left' ? 'pr4' : ''} ${column === 'right' ? 'pl4' : ''}`}>
                <Utr
                    sportTypeId={sportTypeId}
                    player={player}
                    pbrClass={styles.pbr}
                    force={{ ratingType }}
                    data-testid={`match-sheet-card.member-${player.id}.rating`}
                />
            </span>
        );
    };

    const notRated = 'UR';
    const getPlayerDetails = (column) => {
        if (match.position1?.typeId === drawPositionTypeIds.PLAYER) {
            if (column === 'left' && match.position1?.memberIds?.length > 0) {
                const player1 = memberById.get(match.position1.memberIds[0]);
                return player1 ? (
                    <>
                        <div className={`col-sm-12 p-0 justify-content-md-end ${didPosition1Win ? styles.winner : ''}`}>
                            {renderRating(player1, MatchFormatTypes.SINGLES, column)} {player1.firstName} {player1.lastName}
                        </div>
                        {isOfficialHSEvent && (
                            <div className={`col-sm-12 p-0 justify-content-md-end`}>
                                {player1.highSchool}
                            </div>
                        )}
                    </>
                ) : null;
            }
        }

        if (match.position2?.typeId === drawPositionTypeIds.PLAYER) {
            if (column === 'right' && match.position2?.memberIds?.length > 0) {
                const player2 = memberById.get(match.position2.memberIds[0]);
                return player2 ? (
                    <>
                        <div className={`col-sm-12 p-0 ${didPosition2Win ? styles.winner : ''}`}>
                            {player2.firstName} {player2.lastName} {renderRating(player2, MatchFormatTypes.SINGLES, column)}
                        </div>
                        {isOfficialHSEvent && (
                            <div className={`col-sm-12 p-0`}>
                                {player2.highSchool}
                            </div>
                        )}
                    </>
                ) : null;
            }
        }

        if (match.position1?.typeId === drawPositionTypeIds.TEAM) {
            if (column === 'left') {
                const [t1p1Id, t1p2Id] = match.position1.memberIds ?? [];
                const team1Player1 = memberById.get(t1p1Id);
                const team1Player2 = memberById.get(t1p2Id);
                const team1DisplayName = getMatchSheetTeamDisplayName(match.position1, match.participatingMembers);
                const team1Rating = isTennisEvent
                    ? getMatchSheetTeamAvgDoublesUtr(match.position1, match.participatingMembers)
                    : getAveragePickleballRatingDisplayV2(team1Player1, team1Player2);

                return (
                    <>
                        <div className={`col-sm-12 p-0 justify-content-md-end ${didPosition1Win ? styles.winner : ''}`}>
                            {team1Rating ?? notRated} {team1DisplayName}
                        </div>
                        {isOfficialHSEvent && (
                            <div className={`col-sm-12 p-0 justify-content-md-end`}>
                                {team1Player1?.highSchool} {team1Player2?.highSchool && team1Player2?.highSchool !== team1Player1?.highSchool ? `/ ${team1Player2.highSchool}` : ''}
                            </div>
                        )}
                    </>
                );
            }
        }

        if (match.position2?.typeId === drawPositionTypeIds.TEAM) {
            if (column === 'right') {
                const [t2p1Id, t2p2Id] = match.position2.memberIds ?? [];
                const team2Player1 = memberById.get(t2p1Id);
                const team2Player2 = memberById.get(t2p2Id);
                const team2DisplayName = getMatchSheetTeamDisplayName(match.position2, match.participatingMembers);
                const team2Rating = isTennisEvent
                    ? getMatchSheetTeamAvgDoublesUtr(match.position2, match.participatingMembers)
                    : getAveragePickleballRatingDisplayV2(team2Player1, team2Player2);

                return (
                    <>
                        <div className={`col-sm-12 p-0 ${didPosition2Win ? styles.winner : ''}`}>
                            {team2DisplayName} {team2Rating ?? notRated}
                        </div>
                        {isOfficialHSEvent && (
                            <div className={`col-sm-12 p-0`}>
                                {team2Player1?.highSchool} {team2Player2?.highSchool && team2Player2?.highSchool !== team2Player1?.highSchool ? `/ ${team2Player2.highSchool}` : ''}
                            </div>
                        )}
                    </>
                );
            }
        }

        return TBD_DISPLAY;
    };

    const getScores = () => {
        const walkOverValues = ['Walkover', 'Defaulted', 'walkover', 'defaulted', 'Abandoned Match', 'Double Walkover', 'abandoned match', 'double walkover'];
        const score = match.result?.score;

        if (score) {
            const scoreArray = Object.values(score);
            if (scoreArray.length > 0) {
                return (
                    <>
                        {scoreArray.map(scoreItem => {
                            if (didPosition1Win) {
                                return `${scoreItem.winner}-${scoreItem.loser}${getTiebreak(scoreItem)} `;
                            }
                            return `${scoreItem.loser}-${scoreItem.winner}${getTiebreak(scoreItem)} `;
                        })}
                        {match.result.completionType !== 'winnercompleted' && match.result.outcome && `(${capitalize(match.result.outcome)})` }
                    </>
                );
            }
            if ((walkOverValues.includes(match.result.outcome) || (match.result.completionType === 'winnerdidnotstart'))) {
                return (
                    <span className="outcome-font">{(walkOverValues.includes(match.result.outcome) && `(${capitalize(match.result.outcome)})`) || '(Walkover)'}</span>
                );
            }
        }
        if (isCurrentMemberAdmin && !hidePrintButton && match.position1 && match.position2) {
            return (
                <button
                    type="button"
                    className={`btn btn-default btn-sm`}
                    disabled={!postScoreEnabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onPostScoreClick(i);
                    }}
                    data-testid="match-sheet-card.post-score-btn"
                >
                    Post Score
                </button>
            );
        }
        return '';
    };

    const buildRoundLabel = () => {
        const draw = draws.drawsDict[match.drawId];
        return getRoundLabel({
            eliminationTypeId: draw?.eliminationTypeId,
            matchPosition: match.matchPosition,
            roundNumber: match.roundNumber,
            roundSize,
        });
    };

    return (
        <div key={match.id} className={`${styles.matchCard} ${hidePrintButton ? 'border-bottom border-dark' : ''} utr-card p8 bg-white d-flex aic`}>
            <div className={styles.msRow}>
                <div className={`${styles.msCol} ${styles.msColTime}`} data-testid={`match-sheet-card.match-${match.id}.match-time`}>
                    {moment.utc(`${match.date}`).tz(eventTimeZone).format('h:mm A z')}
                </div>
                <div className={`${styles.msCol} ${styles.msColLocation}`} data-testid={`match-sheet-card.match-${match.id}.location`}>
                    {getMatchLocation()}
                </div>
                <div className={`${styles.msCol} ${styles.msColDraw}`} data-testid={`match-sheet-card.match-${match.id}.draw`}>
                    {buildDrawName()}
                </div>
                {showRoundColumn && (
                    <div className={`${styles.msCol} ${styles.msColRound}`} data-testid={`match-sheet-card.match-${match.id}.round`}>
                        {buildRoundLabel()}
                    </div>
                )}
                <div className={`${styles.msCol} ${styles.msColPlayer} d-block text-right justify-content-md-end ${styles.player}`} data-testid={`match-sheet-card.match-${match.id}.player-team-a`}>
                    {getPlayerDetails('left')}
                </div>
                <div className={`${styles.msCol} ${styles.msColPlayer} d-block ${styles.player}`} data-testid={`match-sheet-card.match-${match.id}.player-team-b`}>
                    {getPlayerDetails('right')}
                </div>
                <div className={`${styles.msCol} ${styles.msColResult}`} data-testid={`match-sheet-card.match-${match.id}.match-result`}>
                    {getScores()}
                </div>
            </div>
            <div className={styles.optionsContainer}>
                {!hidePrintButton && menuOptions.length > 0 && match.result && (
                    <PopOverMenu
                        horizontalEllipsis={false}
                        menuItems={menuOptions}
                        onSelected={(menuOption) => onMenuSelected(menuOption, i)}
                        anchorClass="pt4"
                    />
                )}
            </div>
        </div>
    );
};

MatchSheetCard.propTypes = {
    match: PropTypes.shape({
        id: PropTypes.string.isRequired,
        drawId: PropTypes.string.isRequired,
        roundNumber: PropTypes.number,
        matchPosition: PropTypes.number,
        isLive: PropTypes.bool,
        liveMatch: PropTypes.shape({
            status: PropTypes.string,
            gameState: PropTypes.string,
            startTime: PropTypes.string,
            matchDurationSeconds: PropTypes.number,
            periodScores: PropTypes.arrayOf(PropTypes.shape({
                home_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                away_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                homeScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                awayScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                home_tiebreak_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                away_tiebreak_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                tiebreak_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            })),
        }),
        position1: PropTypes.shape({
            typeId: PropTypes.number,
            memberIds: PropTypes.arrayOf(PropTypes.number),
        }),
        position2: PropTypes.shape({
            typeId: PropTypes.number,
            memberIds: PropTypes.arrayOf(PropTypes.number),
        }),
        participatingMembers: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            firstName: PropTypes.string,
            lastName: PropTypes.string,
            highSchool: PropTypes.string,
            singlesUtr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            doublesUtr: PropTypes.number,
            pbrRatingDisplay: PropTypes.shape({
                ratingDisplay: PropTypes.string,
                value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                pbrBallColor: PropTypes.string,
            }),
            utrRange: PropTypes.shape({
                pkbRatingDisplay: PropTypes.string,
                pbrRange: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
            }),
        })),
        result: PropTypes.shape({
            score: PropTypes.objectOf(PropTypes.shape({
                winner: PropTypes.number,
                loser: PropTypes.number,
                tiebreak: PropTypes.number,
                winnerTiebreak: PropTypes.number,
            })),
            completionType: PropTypes.string,
            outcome: PropTypes.string,
        }),
        date: PropTypes.string,
        locationId: PropTypes.string,
        facilityId: PropTypes.string,
        courtId: PropTypes.string,
        courtName: PropTypes.string,
        facilityName: PropTypes.string,
        facilityAbbreviation: PropTypes.string,
    }).isRequired,
    i: PropTypes.number.isRequired,
    eventTimeZone: PropTypes.string,
    hidePrintButton: PropTypes.bool,
    menuOptions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        value: PropTypes.string,
    })),
    onMenuSelected: PropTypes.func,
    isOfficialHSEvent: PropTypes.bool,
    isCurrentMemberAdmin: PropTypes.bool,
    postScoreEnabled: PropTypes.bool,
    onPostScoreClick: PropTypes.func,
    sportTypeId: PropTypes.number,
    drawCategoryTypesDict: PropTypes.object,
    showRoundColumn: PropTypes.bool,
    roundSize: PropTypes.number,
};

MatchSheetCard.defaultProps = {
    eventTimeZone: null,
    hidePrintButton: false,
    menuOptions: [],
    onMenuSelected: () => {},
    isOfficialHSEvent: false,
    isCurrentMemberAdmin: false,
    postScoreEnabled: false,
    onPostScoreClick: () => {},
    sportTypeId: null,
    drawCategoryTypesDict: {},
    showRoundColumn: false,
    roundSize: 0,
};

export default MatchSheetCard;
